import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { adminAPI, careGapAPI } from '../../lib/api';
import { 
  ChartBar, 
  Users, 
  Stethoscope, 
  Receipt,
  Warning,
  TrendUp,
  Clock,
  CheckCircle
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#0D9488', '#0284C7', '#7C3AED', '#D97706'];

export default function AnalyticsPage() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [careGaps, setCareGaps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [analyticsRes, careGapsRes] = await Promise.all([
        adminAPI.getAnalytics(),
        careGapAPI.getAll(),
      ]);
      setAnalytics(analyticsRes.data);
      setCareGaps(careGapsRes.data);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const userRoleData = analytics?.users?.by_role ? [
    { name: 'Patients', value: analytics.users.by_role.patients, color: '#0D9488' },
    { name: 'CMAs', value: analytics.users.by_role.cmas, color: '#0284C7' },
    { name: 'Providers', value: analytics.users.by_role.providers, color: '#7C3AED' },
    { name: 'Admins', value: analytics.users.by_role.admins, color: '#D97706' },
  ].filter(d => d.value > 0) : [];

  const encounterData = [
    { name: 'Total', value: analytics?.encounters?.total || 0 },
    { name: 'Completed', value: analytics?.encounters?.completed || 0 },
    { name: 'Pending', value: (analytics?.encounters?.total || 0) - (analytics?.encounters?.completed || 0) },
  ];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Analytics Dashboard
        </h1>
        <p className="text-slate-500 mt-1">Comprehensive overview of system performance</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="kpi-card card-hover" data-testid="analytics-total-users">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value">{analytics?.users?.total || 0}</div>
              <div className="kpi-label">Total Users</div>
            </div>
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
              <Users size={24} className="text-teal-600" />
            </div>
          </div>
        </div>

        <div className="kpi-card card-hover" data-testid="analytics-encounters">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value">{analytics?.encounters?.total || 0}</div>
              <div className="kpi-label">Total Encounters</div>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Stethoscope size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="kpi-card card-hover" data-testid="analytics-revenue">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value">${analytics?.billing?.total_revenue?.toFixed(0) || 0}</div>
              <div className="kpi-label">Revenue</div>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Receipt size={24} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="kpi-card card-hover" data-testid="analytics-care-gaps">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value text-amber-600">{analytics?.care_gaps?.open || 0}</div>
              <div className="kpi-label">Open Care Gaps</div>
            </div>
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <Warning size={24} className="text-amber-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* User Distribution */}
        <div className="card-clinical p-6">
          <h3 className="font-semibold text-slate-900 mb-6">User Distribution by Role</h3>
          {userRoleData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={userRoleData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {userRoleData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-500">
              No user data available
            </div>
          )}
        </div>

        {/* Encounter Stats */}
        <div className="card-clinical p-6">
          <h3 className="font-semibold text-slate-900 mb-6">Encounter Statistics</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={encounterData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fill: '#64748B' }} />
                <YAxis tick={{ fill: '#64748B' }} />
                <Tooltip />
                <Bar dataKey="value" fill="#0D9488" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="card-clinical p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-900">Completion Rate</h4>
              <p className="text-sm text-slate-500">Encounters completed</p>
            </div>
          </div>
          <div className="text-3xl font-bold text-green-600">
            {analytics?.encounters?.completion_rate || 0}%
          </div>
          <div className="mt-2 h-2 bg-slate-100 rounded-full">
            <div 
              className="h-2 bg-green-500 rounded-full transition-all"
              style={{ width: `${analytics?.encounters?.completion_rate || 0}%` }}
            />
          </div>
        </div>

        <div className="card-clinical p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock size={20} className="text-amber-600" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-900">Pending Intakes</h4>
              <p className="text-sm text-slate-500">Awaiting triage</p>
            </div>
          </div>
          <div className="text-3xl font-bold text-amber-600">
            {analytics?.intakes?.pending || 0}
          </div>
        </div>

        <div className="card-clinical p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendUp size={20} className="text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-900">Outstanding Balance</h4>
              <p className="text-sm text-slate-500">Unpaid invoices</p>
            </div>
          </div>
          <div className="text-3xl font-bold text-blue-600">
            ${analytics?.billing?.outstanding_balance?.toFixed(0) || 0}
          </div>
        </div>
      </div>

      {/* Care Gaps */}
      <div className="card-clinical">
        <div className="p-6 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Warning size={18} className="text-amber-500" />
            Open Care Gaps
          </h3>
        </div>
        
        {careGaps.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle size={48} className="text-green-300 mx-auto mb-4" />
            <p className="text-slate-500">No open care gaps</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-clinical">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Gap Type</th>
                  <th>Description</th>
                  <th>Priority</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {careGaps.slice(0, 10).map((gap) => (
                  <tr key={gap.id}>
                    <td className="font-medium">{gap.patient_name}</td>
                    <td className="capitalize">{gap.gap_type}</td>
                    <td className="max-w-xs truncate">{gap.description}</td>
                    <td>
                      <span className={`status-badge ${
                        gap.priority === 'high' ? 'status-urgent' :
                        gap.priority === 'medium' ? 'status-pending' : 'status-completed'
                      }`}>
                        {gap.priority}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${gap.status === 'open' ? 'status-in-progress' : 'status-completed'}`}>
                        {gap.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
